import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Divider,
  Empty,
  Form,
  Input,
  InputNumber,
  List,
  message,
  Modal,
  Progress,
  Row,
  Col,
  Space,
  Spin,
  Steps,
  Table,
  Tooltip,
  Typography,
  Select,
} from 'antd';
import {
  DownloadOutlined,
  UploadOutlined,
  FileExcelOutlined,
  DeleteOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { read, utils, writeFile } from 'xlsx';
import {
  PRODUCT_IMPORT_EXPORT_HEADERS,
  PRODUCT_IMPORT_FIELD_KEYS,
  PRODUCT_IMPORT_FIELD_LABELS,
  type ProductImportDraftRow,
  type ProductImportFieldKey,
  type ProductImportColumnMapping,
  matchCategoryIdByName,
  parseProductImportMatrixWithMapping,
  guessHeaderRowIndex,
  suggestMappingFromHeaderRow,
  validateColumnMappingDuplicates,
  columnIndexToExcelLetters,
  validateProductImportDrafts,
  issuesByRowIndex,
  hasBlockingIssues,
} from '../utils/productImportExport';
import {
  useProductsForBusinessId,
  useSuperAdminBulkImportProducts,
  useSuperAdminPurgeAllProductsForBusiness,
  useSuperAdminApplyMinStockAlertToAllProducts,
  type BulkImportProgressEvent,
} from '../../../hooks/useProducts';
import { useProductCategoriesForBusinessId } from '../../../hooks/useProductCategories';
import { useDefaultPointOfSaleSite, useSites } from '../../../hooks/useSites';
import { useAuth } from '../../../contexts/AuthContext';
import { useBusinesses, useBusiness } from '../../../hooks/useBusinesses';
import { getPackagingUnits } from '../../../types/business';
import { getPackagingUnitsFromList } from '../../../utils/productPackaging';
import { DEFAULT_BUSINESS_CONFIGURATION } from '../../../types/business';
import { buildSourceMatchSelectOptions, rankSimilarSourceProducts } from '../utils/sourceProductMatch';
const { Text, Paragraph } = Typography;

function toInt(n: unknown, fallback: number): number {
  const x = typeof n === 'number' ? n : Number(n);
  if (!Number.isFinite(x)) return fallback;
  return Math.round(x);
}

type Props = {
  businessId: string;
  businessName: string;
  /** Super Admin ou CSM lié au business */
  canUse: boolean;
  inventoryEnabled: boolean;
  /** Seuil alerte stock si absent / colonne vide dans le fichier (config inventaire du business). */
  defaultMinStockAlert?: number;
  /** Multi-sites inventaire : pour appliquer le seuil aussi par lieu (lieux actifs). */
  multiSitesEnabled?: boolean;
};

const ProductsImportExportTabContent: React.FC<Props> = ({
  businessId,
  businessName,
  canUse,
  inventoryEnabled,
  defaultMinStockAlert: defaultMinStockAlertProp,
  multiSitesEnabled = false,
}) => {
  const defaultMinStockAlert =
    defaultMinStockAlertProp ??
    DEFAULT_BUSINESS_CONFIGURATION.inventory.lowStockThreshold;
  const { isSuperAdmin, userProfile } = useAuth();
  const [sourceBusinessId, setSourceBusinessId] = useState<string | undefined>(undefined);
  const {
    data: businessesList = [],
    isPending: businessesListPending,
    isFetching: businessesListFetching,
  } = useBusinesses();
  const { data: targetBusiness } = useBusiness(businessId);
  const allowedPackagingUnits = useMemo(
    () => getPackagingUnitsFromList(getPackagingUnits(targetBusiness ?? null)),
    [targetBusiness]
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  /** Matrice brute du fichier (première feuille). */
  const [importMatrix, setImportMatrix] = useState<unknown[][] | null>(null);
  /** true = fichier chargé, correspondance à valider ou en cours d’édition. */
  const [awaitingColumnMapping, setAwaitingColumnMapping] = useState(false);
  const [headerRowIndex, setHeaderRowIndex] = useState(0);
  const [columnMappingForm, setColumnMappingForm] = useState<
    Partial<Record<ProductImportFieldKey, number>>
  >({});
  const [draftRows, setDraftRows] = useState<ProductImportDraftRow[]>([]);
  /** false après correspondance colonnes si une autre boutique existe : afficher l’étape « boutique de référence » avant le tableau. */
  const [previewReady, setPreviewReady] = useState(false);
  const [purgeModalOpen, setPurgeModalOpen] = useState(false);
  const [purgeConfirmName, setPurgeConfirmName] = useState('');
  const [bulkMinStockModalOpen, setBulkMinStockModalOpen] = useState(false);
  const [bulkMinStockValue, setBulkMinStockValue] = useState<number | null>(5);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importProgress, setImportProgress] = useState<{
    total: number;
    processed: number;
    label: string;
    errors: { rowIndex: number; name: string; message: string }[];
    phase: 'running' | 'done';
    created: number;
    fatalMessage?: string;
  } | null>(null);
  const importMutation = useSuperAdminBulkImportProducts();
  const purgeProductsMutation = useSuperAdminPurgeAllProductsForBusiness();
  const applyMinStockAllMutation = useSuperAdminApplyMinStockAlertToAllProducts();

  const { data: products = [], isLoading: productsLoading } = useProductsForBusinessId(businessId, {
    enabled: canUse && inventoryEnabled,
  });
  const { data: categories = [], isLoading: categoriesLoading } = useProductCategoriesForBusinessId(
    businessId,
    { enabled: canUse && inventoryEnabled }
  );
  const defaultPos = useDefaultPointOfSaleSite(businessId);
  const { data: sites = [] } = useSites(businessId);
  const activeSiteIds = useMemo(
    () => sites.filter((s) => s.isActive !== false).map((s) => s.id),
    [sites]
  );

  const sourceBusinessOptions = useMemo(() => {
    const allowed = isSuperAdmin
      ? businessesList
      : businessesList.filter((b) => userProfile?.businessIds?.includes(b.id));
    return allowed
      .filter((b) => b.id !== businessId)
      .map((b) => ({ value: b.id, label: b.name }));
  }, [businessesList, businessId, isSuperAdmin, userProfile?.businessIds]);

  /** Au moins une autre boutique active : sinon pas de rapprochement inter-business (colonne + sélecteur masqués). */
  const hasReferenceBusiness = sourceBusinessOptions.length > 0;

  const { data: sourceProducts = [], isLoading: sourceProductsLoading } = useProductsForBusinessId(
    sourceBusinessId,
    { enabled: !!sourceBusinessId && canUse && inventoryEnabled && hasReferenceBusiness }
  );

  useEffect(() => {
    if (!categories.length) return;
    setDraftRows((prev) => {
      let changed = false;
      const next = prev.map((row) => {
        if (row.selectedCategoryId) return row;
        const m = matchCategoryIdByName(categories, row.importCategoryName);
        if (m) {
          changed = true;
          return { ...row, selectedCategoryId: m };
        }
        return row;
      });
      return changed ? next : prev;
    });
  }, [categories]);

  useEffect(() => {
    if (!sourceBusinessId) {
      setDraftRows((prev) =>
        prev.map((r) => ({ ...r, selectedSourceProductId: undefined }))
      );
    }
  }, [sourceBusinessId]);

  useEffect(() => {
    if (!hasReferenceBusiness) {
      setSourceBusinessId(undefined);
      setDraftRows((prev) =>
        prev.map((r) => ({ ...r, selectedSourceProductId: undefined }))
      );
    }
  }, [hasReferenceBusiness]);

  // NB: l’étape « Boutique de référence » doit toujours être visible (optionnelle),
  // donc on ne passe jamais automatiquement à l’aperçu.

  const existingProductNamesLower = useMemo(() => {
    const s = new Set<string>();
    for (const p of products) {
      if (p.name?.trim()) s.add(p.name.trim().toLowerCase());
    }
    return s;
  }, [products]);

  const existingBarcodesLower = useMemo(() => {
    const s = new Set<string>();
    for (const p of products) {
      if (p.barcode?.trim()) s.add(p.barcode.trim().toLowerCase());
    }
    return s;
  }, [products]);

  const maxColumnCount = useMemo(() => {
    if (!importMatrix?.length) return 0;
    let m = 0;
    for (const row of importMatrix) {
      m = Math.max(m, row.length);
    }
    return m;
  }, [importMatrix]);

  const columnSelectOptions = useMemo(() => {
    if (!importMatrix || maxColumnCount <= 0) return [];
    const header = importMatrix[headerRowIndex] || [];
    const opts: { value: number; label: string }[] = [];
    for (let c = 0; c < maxColumnCount; c++) {
      const raw = String(header[c] ?? '').trim();
      const preview = raw.length > 48 ? `${raw.slice(0, 45)}…` : raw;
      opts.push({
        value: c,
        label: `${columnIndexToExcelLetters(c)} — ${preview || '(vide)'}`,
      });
    }
    return opts;
  }, [importMatrix, headerRowIndex, maxColumnCount]);

  const validationIssues = useMemo(
    () =>
      validateProductImportDrafts(
        draftRows,
        categories,
        existingProductNamesLower,
        existingBarcodesLower,
        allowedPackagingUnits
      ),
    [draftRows, categories, existingProductNamesLower, existingBarcodesLower, allowedPackagingUnits]
  );
  const issueMap = useMemo(() => issuesByRowIndex(validationIssues), [validationIssues]);
  const blocked =
    hasBlockingIssues(validationIssues) ||
    draftRows.length === 0 ||
    awaitingColumnMapping ||
    (draftRows.length > 0 && !previewReady);

  const importFlowStarted = Boolean(importMatrix || draftRows.length > 0);
  const importStepCurrent = awaitingColumnMapping ? 0 : draftRows.length === 0 ? 0 : previewReady ? 2 : 1;

  const updateRow = useCallback((rowIndex: number, patch: Partial<ProductImportDraftRow>) => {
    setDraftRows((prev) =>
      prev.map((r) => (r.rowIndex === rowIndex ? { ...r, ...patch } : r))
    );
  }, []);

  const applySourceProductToRow = useCallback(
    (row: ProductImportDraftRow, sourceProductId: string | undefined) => {
      if (!sourceProductId) {
        updateRow(row.rowIndex, { selectedSourceProductId: undefined, sourceMatchDisabled: true });
        return;
      }

      if (sourceProductId === '__auto__') {
        updateRow(row.rowIndex, { selectedSourceProductId: undefined, sourceMatchDisabled: false });
        const ranked = rankSimilarSourceProducts(row.name, sourceProducts, {
          minSimilarity: 0.6,
          limit: 1,
        });
        const best = ranked[0];
        if (!best?.id) return;
        // Applique le meilleur candidat immédiatement
        const src = sourceProducts.find((p) => p.id === best.id);
        if (!src) return;
        const srcCategoryName = String((src as any)?.categoryName ?? '').trim();
        const matchedCategoryId =
          !row.selectedCategoryId && categories.length > 0 && srcCategoryName
            ? matchCategoryIdByName(categories, srcCategoryName)
            : undefined;
        updateRow(row.rowIndex, {
          selectedSourceProductId: best.id,
          sourceMatchDisabled: false,
          sellingPrice: row.sellingPrice ?? src.sellingPrice,
          costPrice: row.costPrice ?? src.costPrice,
          brand: row.brand?.trim() ? row.brand : src.brand ?? '',
          description: row.description?.trim() ? row.description : src.description ?? '',
          ...(matchedCategoryId ? { selectedCategoryId: matchedCategoryId } : {}),
        });
        return;
      }

      const src = sourceProducts.find((p) => p.id === sourceProductId);
      if (!src) {
        updateRow(row.rowIndex, { selectedSourceProductId: sourceProductId, sourceMatchDisabled: false });
        return;
      }

      const srcCategoryName = String((src as any)?.categoryName ?? '').trim();
      const matchedCategoryId =
        !row.selectedCategoryId && categories.length > 0 && srcCategoryName
          ? matchCategoryIdByName(categories, srcCategoryName)
          : undefined;

      updateRow(row.rowIndex, {
        selectedSourceProductId: sourceProductId,
        sourceMatchDisabled: false,
        // Remplir uniquement les champs manquants côté fichier (prévisualisation)
        sellingPrice: row.sellingPrice ?? src.sellingPrice,
        costPrice: row.costPrice ?? src.costPrice,
        brand: row.brand?.trim() ? row.brand : src.brand ?? '',
        description: row.description?.trim() ? row.description : src.description ?? '',
        ...(matchedCategoryId ? { selectedCategoryId: matchedCategoryId } : {}),
      });
    },
    [sourceProducts, updateRow, categories]
  );

  // Auto-sélection du "meilleur match" par nom (≥ 60%) pour accélérer le rapprochement.
  // Ne doit pas écraser un choix manuel déjà fait sur une ligne.
  useEffect(() => {
    if (!previewReady) return;
    if (!hasReferenceBusiness) return;
    if (!sourceBusinessId) return;
    if (sourceProductsLoading) return;
    if (!sourceProducts.length) return;

    const byId = new Map(sourceProducts.map((p) => [p.id, p] as const));

    setDraftRows((prev) => {
      let changed = false;
      const next = prev.map((r) => {
        if (r.sourceMatchDisabled) return r;
        if (r.selectedSourceProductId) return r;
        const ranked = rankSimilarSourceProducts(r.name, sourceProducts, {
          minSimilarity: 0.6,
          limit: 1,
        });
        const best = ranked[0];
        if (!best?.id) return r;
        const src = byId.get(best.id);
        if (!src) return r;
        const srcCategoryName = String((src as any)?.categoryName ?? '').trim();
        const matchedCategoryId =
          !r.selectedCategoryId && categories.length > 0 && srcCategoryName
            ? matchCategoryIdByName(categories, srcCategoryName)
            : undefined;
        changed = true;
        return {
          ...r,
          selectedSourceProductId: best.id,
          sourceMatchDisabled: false,
          sellingPrice: r.sellingPrice ?? src.sellingPrice,
          costPrice: r.costPrice ?? src.costPrice,
          brand: r.brand?.trim() ? r.brand : (src.brand ?? r.brand),
          description: r.description?.trim() ? r.description : (src.description ?? r.description),
          ...(matchedCategoryId ? { selectedCategoryId: matchedCategoryId } : {}),
        };
      });
      return changed ? next : prev;
    });
  }, [
    previewReady,
    hasReferenceBusiness,
    sourceBusinessId,
    sourceProductsLoading,
    sourceProducts,
    categories,
  ]);

  const columns = useMemo(() => {
    const sourceReferenceColumn = {
      title: 'Produit référence',
      key: 'sourceMatch',
      width: 300,
      fixed: 'left' as const,
      render: (_: unknown, r: ProductImportDraftRow) => {
        if (!sourceBusinessId) {
          return (
            <Tooltip title="Choisissez d’abord une « Boutique source » dans la liste au-dessus du tableau.">
              <span style={{ display: 'block', width: '100%' }}>
                <Select
                  disabled
                  placeholder="Choisir la boutique source…"
                  style={{ minWidth: 260, maxWidth: 380, width: '100%' }}
                />
              </span>
            </Tooltip>
          );
        }
        const { auto, similar } = buildSourceMatchSelectOptions(r.name, sourceProducts);
        const grouped: {
          label: string;
          options: { value: string; label: string }[];
        }[] = [{ label: 'Automatique', options: [auto] }];
        if (similar.length > 0) {
          grouped.push({ label: 'Similaires (nom)', options: similar });
        }
        return (
          <Select
            showSearch
            allowClear
            optionFilterProp="label"
            placeholder="Rapprochement…"
            loading={sourceProductsLoading}
            value={r.selectedSourceProductId ?? undefined}
            options={grouped}
            onChange={(v) => applySourceProductToRow(r, v === undefined ? undefined : String(v))}
            style={{ minWidth: 260, maxWidth: 380, width: '100%' }}
            listHeight={280}
          />
        );
      },
    };

    return [
      {
        title: 'Ligne',
        dataIndex: 'rowIndex',
        width: 72,
        fixed: 'left' as const,
      },
      {
        title: 'Nom',
        dataIndex: 'name',
        width: 280,
        fixed: 'left' as const,
        render: (_: unknown, r: ProductImportDraftRow) => (
          <Input.TextArea
            value={r.name}
            onChange={(e) => updateRow(r.rowIndex, { name: e.target.value })}
            autoSize={{ minRows: 2, maxRows: 12 }}
            style={{ width: '100%', minWidth: 240, resize: 'vertical' as const }}
            status={issueMap.get(r.rowIndex)?.some((m) => m.includes('nom')) ? 'error' : undefined}
          />
        ),
      },
      ...(hasReferenceBusiness && sourceBusinessId ? [sourceReferenceColumn] : []),
      {
        title: 'Catégorie (fichier)',
        dataIndex: 'importCategoryName',
        width: 160,
        render: (v: string) => <Text type="secondary">{v || '—'}</Text>,
      },
      {
        title: 'Catégorie',
        key: 'category',
        width: 240,
        render: (_: unknown, r: ProductImportDraftRow) => (
          <Select
            showSearch
            allowClear
            placeholder="Choisir…"
            optionFilterProp="label"
            style={{ minWidth: 200 }}
            value={r.selectedCategoryId}
            options={categories.map((c) => ({ value: c.id, label: c.name }))}
            onChange={(v) => updateRow(r.rowIndex, { selectedCategoryId: v || undefined })}
            status={
              issueMap.get(r.rowIndex)?.some((m) => m.includes('catégorie') || m.includes('Catégorie'))
                ? 'error'
                : undefined
            }
          />
        ),
      },
      {
        title: 'Code-barres',
        dataIndex: 'barcode',
        width: 130,
        render: (_: unknown, r: ProductImportDraftRow) => (
          <Input
            value={r.barcode}
            onChange={(e) => updateRow(r.rowIndex, { barcode: e.target.value })}
          />
        ),
      },
      {
        title: 'Prix vente',
        width: 110,
        render: (_: unknown, r: ProductImportDraftRow) => (
          <InputNumber
            min={0}
            value={r.sellingPrice}
            onChange={(v) => updateRow(r.rowIndex, { sellingPrice: v === null ? undefined : Number(v) })}
            style={{ width: '100%' }}
          />
        ),
      },
      {
        title: 'Prix achat',
        width: 110,
        render: (_: unknown, r: ProductImportDraftRow) => (
          <InputNumber
            min={0}
            value={r.costPrice}
            onChange={(v) => updateRow(r.rowIndex, { costPrice: v === null ? undefined : Number(v) })}
            style={{ width: '100%' }}
          />
        ),
      },
      {
        title: 'Seuil',
        width: 88,
        render: (_: unknown, r: ProductImportDraftRow) => (
          <InputNumber
            min={0}
            precision={0}
            value={r.minStockAlert}
            onChange={(v) => updateRow(r.rowIndex, { minStockAlert: toInt(v, 0) })}
            style={{ width: '100%' }}
          />
        ),
      },
      {
        title: 'Stock',
        width: 88,
        render: (_: unknown, r: ProductImportDraftRow) => (
          <InputNumber
            min={0}
            precision={0}
            value={r.currentStock}
            onChange={(v) => updateRow(r.rowIndex, { currentStock: toInt(v, 0) })}
            style={{ width: '100%' }}
          />
        ),
      },
      {
        title: 'Erreurs',
        key: 'errs',
        width: 260,
        render: (_: unknown, r: ProductImportDraftRow) => {
          const msgs = issueMap.get(r.rowIndex);
          if (!msgs?.length) return <Text type="success">OK</Text>;
          return (
            <Text type="danger" style={{ fontSize: 12 }}>
              {msgs.join(' ')}
            </Text>
          );
        },
      },
    ];
  }, [
    issueMap,
    categories,
    updateRow,
    applySourceProductToRow,
    sourceBusinessId,
    hasReferenceBusiness,
    sourceProducts,
    sourceProductsLoading,
  ]);

  const applyColumnMapping = useCallback(() => {
    if (!importMatrix) return;
    const nameCol = columnMappingForm.name;
    const catCol = columnMappingForm.category;
    if (nameCol === undefined || catCol === undefined) {
      message.error('Les champs « Nom du produit » et « Catégorie » sont obligatoires.');
      return;
    }
    const full: ProductImportColumnMapping = {
      name: nameCol,
      category: catCol,
    };
    for (const key of PRODUCT_IMPORT_FIELD_KEYS) {
      if (key === 'name' || key === 'category') continue;
      const v = columnMappingForm[key];
      if (v !== undefined) full[key] = v;
    }
    const dupErr = validateColumnMappingDuplicates(full);
    if (dupErr) {
      message.error(dupErr);
      return;
    }
    try {
      const parsed = parseProductImportMatrixWithMapping(importMatrix, headerRowIndex, full, {
        defaultMinStockAlert,
      });
      const withCategories: ProductImportDraftRow[] = parsed.map((row) => ({
        ...row,
        selectedCategoryId:
          categories.length > 0 ? matchCategoryIdByName(categories, row.importCategoryName) : undefined,
      }));
      setSourceBusinessId(undefined);
      setDraftRows(withCategories);
      setAwaitingColumnMapping(false);
      setPreviewReady(false);
      const otherExists = businessesList.some((b) => {
        if (b.id === businessId) return false;
        if (!isSuperAdmin && !userProfile?.businessIds?.includes(b.id)) return false;
        return true;
      });
      message.success(
        otherExists
          ? `${withCategories.length} ligne(s) analysée(s). Étape suivante : boutique de référence (optionnel), puis affichez l’aperçu.`
          : `${withCategories.length} ligne(s) — ouverture de l’aperçu…`
      );
    } catch (err: unknown) {
      message.error(err instanceof Error ? err.message : 'Erreur lors de l’analyse du fichier.');
    }
  }, [
    importMatrix,
    headerRowIndex,
    columnMappingForm,
    categories,
    defaultMinStockAlert,
    businessesList,
    businessId,
    isSuperAdmin,
    userProfile?.businessIds,
  ]);

  const handleDownloadTemplate = () => {
    const ws = utils.aoa_to_sheet([Array.from(PRODUCT_IMPORT_EXPORT_HEADERS)]);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Produits');
    writeFile(wb, `modele-produits-${businessName.replace(/\s+/g, '_')}.xlsx`);
  };

  const handleExportProducts = () => {
    if (!products.length) {
      message.warning('Aucun produit actif à exporter.');
      return;
    }
    const rows: (string | number | undefined)[][] = [Array.from(PRODUCT_IMPORT_EXPORT_HEADERS)];
    for (const p of products) {
      rows.push([
        p.name,
        p.categoryName ?? '',
        p.barcode ?? '',
        p.sellingPrice ?? '',
        p.costPrice ?? '',
        p.minStockAlert ?? 0,
        p.currentStock ?? 0,
        p.brand ?? '',
        p.description ?? '',
      ]);
    }
    const ws = utils.aoa_to_sheet(rows);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Produits');
    writeFile(wb, `export-produits-${businessName.replace(/\s+/g, '_')}.xlsx`);
    message.success('Export généré.');
  };

  const loadFileForColumnMapping = async (file: File) => {
    const buf = await file.arrayBuffer();
    const wb = read(buf, { type: 'array' });
    const sheetName = wb.SheetNames[0];
    if (!sheetName) {
      message.error('Fichier vide.');
      return;
    }
    const sheet = wb.Sheets[sheetName];
    const matrix = utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: '' }) as unknown[][];
    const hi = guessHeaderRowIndex(matrix);
    const suggested = suggestMappingFromHeaderRow(matrix[hi] || []);
    setImportMatrix(matrix);
    setHeaderRowIndex(hi);
    setColumnMappingForm(suggested);
    setDraftRows([]);
    setPreviewReady(false);
    setSourceBusinessId(undefined);
    setAwaitingColumnMapping(true);
    message.info('Associez chaque champ aux colonnes du fichier, puis validez.');
  };

  const onPickFile: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      await loadFileForColumnMapping(file);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Lecture du fichier impossible.';
      message.error(msg);
    }
  };

  const submitImport = async () => {
    if (blocked || importMutation.isPending) return;
    const rows = draftRows.map((r) => ({
      name: r.name.trim(),
      categoryId: r.selectedCategoryId!,
      barcode: r.barcode.trim() || undefined,
      sellingPrice: r.sellingPrice,
      costPrice: r.costPrice,
      minStockAlert: toInt(r.minStockAlert, 0),
      currentStock: toInt(r.currentStock, 0),
      brand: r.brand.trim() || undefined,
      description: r.description.trim() || undefined,
      packagingValue: r.packagingValue,
      packagingUnit: r.packagingUnit?.trim() || undefined,
      sourceProductId:
        sourceBusinessId?.trim() && r.selectedSourceProductId
          ? r.selectedSourceProductId
          : undefined,
    }));

    setImportProgress({
      total: rows.length,
      processed: 0,
      label: '',
      errors: [],
      phase: 'running',
      created: 0,
    });
    setImportModalOpen(true);

    const applyProgress = (e: BulkImportProgressEvent) => {
      setImportProgress((prev) => {
        if (!prev) return prev;
        switch (e.kind) {
          case 'start':
            return { ...prev, total: e.total };
          case 'row':
            return { ...prev, label: e.name };
          case 'success':
            return {
              ...prev,
              processed: e.index + 1,
              label: e.name,
            };
          case 'fail':
            return {
              ...prev,
              processed: e.index + 1,
              label: e.name,
              errors: [
                ...prev.errors,
                { rowIndex: e.index, name: e.name, message: e.message },
              ],
            };
          case 'done':
            return {
              ...prev,
              phase: 'done',
              created: e.created,
              errors: e.errors,
            };
          default:
            return prev;
        }
      });
    };

    try {
      const res = await importMutation.mutateAsync({
        businessId,
        rows,
        sourceBusinessId: sourceBusinessId?.trim() || undefined,
        onProgress: applyProgress,
      });
      if (res.errors.length === 0) {
        message.success(`${res.created} produit(s) créé(s).`);
        setDraftRows([]);
      } else {
        message.warning(
          `${res.created} produit(s) créé(s), ${res.errors.length} ligne(s) en erreur — voir le détail ci-dessous.`
        );
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erreur à l’import.';
      setImportProgress((prev) =>
        prev
          ? { ...prev, phase: 'done', fatalMessage: msg }
          : {
              total: rows.length,
              processed: 0,
              label: '',
              errors: [],
              phase: 'done',
              created: 0,
              fatalMessage: msg,
            }
      );
      message.error(msg);
    }
  };

  const closeImportModal = () => {
    setImportModalOpen(false);
    setImportProgress(null);
  };

  const runPurgeProducts = async () => {
    if (purgeConfirmName.trim() !== businessName.trim()) {
      message.error('Le nom saisi ne correspond pas exactement au nom du business.');
      return;
    }
    try {
      const res = await purgeProductsMutation.mutateAsync({ businessId });
      setPurgeModalOpen(false);
      setPurgeConfirmName('');
      message.success(
        `${res.deleted} produit(s) supprimé(s) (ventes et mouvements de stock liés supprimés).`
      );
    } catch (err: unknown) {
      message.error(err instanceof Error ? err.message : 'Erreur lors de la suppression.');
    }
  };

  const handleConfirmBulkMinStock = async () => {
    if (bulkMinStockValue === null || Number.isNaN(bulkMinStockValue)) {
      message.warning('Veuillez saisir un seuil valide.');
      return;
    }
    const threshold = Math.max(0, Math.floor(Number(bulkMinStockValue)));
    const siteIds =
      multiSitesEnabled && activeSiteIds.length > 0 ? activeSiteIds : undefined;
    const hideLoading = message.loading('Mise à jour des seuils d’alerte stock…', 0);
    try {
      const res = await applyMinStockAllMutation.mutateAsync({
        businessId,
        minStockAlert: threshold,
        activeSiteIds: siteIds,
      });
      if (res.errorCount === 0) {
        message.success(`Seuil appliqué à ${res.successCount} produit(s).`);
        setBulkMinStockModalOpen(false);
      } else {
        message.warning(
          `Terminé avec des erreurs : ${res.successCount} réussi(s), ${res.errorCount} échec(s) sur ${res.scanned} produit(s) parcouru(s).`
        );
      }
    } catch (err: unknown) {
      message.error(err instanceof Error ? err.message : 'Erreur lors de la mise à jour des seuils.');
    } finally {
      hideLoading();
    }
  };

  if (!canUse) {
    return (
      <Card>
        <Empty description="Réservé au Super Admin ou au CSM associé à ce business." />
      </Card>
    );
  }

  if (!inventoryEnabled) {
    return (
      <Card>
        <Empty description="Le module Stock / Inventaire n’est pas activé pour ce business." />
      </Card>
    );
  }

  const loading =
    productsLoading || categoriesLoading || (!!sourceBusinessId && sourceProductsLoading);

  return (
    <div className="business-detail-tab">
      <Card title="Produits — import / export Excel">
        {!defaultPos?.id && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Aucun lieu actif"
            description="Créez et activez au moins un lieu dans l’onglet Lieux avant d’importer des produits."
          />
        )}

        {categories.length === 0 && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Aucune catégorie produit active"
            description="Créez des catégories dans l’espace inventaire du business avant l’import."
          />
        )}

        <Paragraph type="secondary" style={{ marginBottom: 16 }}>
          <strong>Import :</strong> fichier → <strong>correspondance des colonnes</strong> → si d’autres boutiques
          existent, <strong>choix d’une boutique de référence</strong> (optionnel) → <strong>aperçu</strong> des
          lignes avec colonne « Produit référence » (produits similaires) pour compléter prix / marque / description.
          Sinon, création à partir du fichier seul. La création en masse reste bloquée tant qu’il reste une erreur.
        </Paragraph>

        {importFlowStarted && (
          <Steps
            size="small"
            current={importStepCurrent}
            style={{ marginBottom: 20 }}
            items={[
              { title: 'Correspondance des colonnes' },
              { title: 'Boutique de référence (optionnel)' },
              { title: 'Aperçu et validation' },
            ]}
          />
        )}

        <Space wrap style={{ marginBottom: 16 }}>
          <Button icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>
            Télécharger le modèle
          </Button>
          <Button type="primary" icon={<FileExcelOutlined />} onClick={handleExportProducts}>
            Exporter les produits
          </Button>
          <Button icon={<UploadOutlined />} onClick={() => fileInputRef.current?.click()}>
            Charger un fichier
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
            aria-label="Importer un fichier Excel de produits"
            onChange={onPickFile}
          />
        </Space>

        {importMatrix && awaitingColumnMapping && (
          <Card type="inner" title="Étape 1 — Correspondance des colonnes" style={{ marginBottom: 16 }}>
            <Paragraph type="secondary" style={{ marginBottom: 12 }}>
              Choisissez la ligne contenant les <strong>en-têtes</strong> (souvent la ligne 1), puis associez chaque
              champ aux colonnes du fichier. Les libellés entre parenthèses viennent de cette ligne. Au moins{' '}
              <Text strong>Nom</Text> et <Text strong>Catégorie</Text> sont requis.
            </Paragraph>
            <Form layout="vertical">
              <Form.Item label="Ligne d’en-têtes (1 = première ligne du fichier)">
                <Select
                  value={headerRowIndex}
                  style={{ maxWidth: 360 }}
                  onChange={(v) => {
                    setHeaderRowIndex(v);
                    setColumnMappingForm(suggestMappingFromHeaderRow(importMatrix[v] || []));
                  }}
                  options={Array.from({ length: Math.min(importMatrix.length, 50) }, (_, i) => ({
                    value: i,
                    label: `Ligne ${i + 1}`,
                  }))}
                />
              </Form.Item>
              <Row gutter={[16, 0]}>
                {PRODUCT_IMPORT_FIELD_KEYS.map((fieldKey) => {
                  const required = fieldKey === 'name' || fieldKey === 'category';
                  return (
                    <Col xs={24} md={12} key={fieldKey}>
                      <Form.Item
                        label={PRODUCT_IMPORT_FIELD_LABELS[fieldKey]}
                        required={required}
                        style={{ marginBottom: 12 }}
                      >
                        <Select
                          showSearch
                          optionFilterProp="label"
                          placeholder={required ? 'Colonne…' : '— Ignorer —'}
                          allowClear={!required}
                          style={{ width: '100%' }}
                          value={columnMappingForm[fieldKey]}
                          onChange={(v) =>
                            setColumnMappingForm((prev) => ({
                              ...prev,
                              [fieldKey]: v === undefined || v === null ? undefined : Number(v),
                            }))
                          }
                          options={columnSelectOptions}
                        />
                      </Form.Item>
                    </Col>
                  );
                })}
              </Row>
              <Space wrap>
                <Button type="primary" onClick={applyColumnMapping}>
                  Valider la correspondance
                </Button>
                <Button
                  onClick={() => {
                    setImportMatrix(null);
                    setAwaitingColumnMapping(false);
                    setColumnMappingForm({});
                    setDraftRows([]);
                    setPreviewReady(false);
                    setSourceBusinessId(undefined);
                  }}
                >
                  Annuler le fichier
                </Button>
              </Space>
            </Form>
          </Card>
        )}

        {importMatrix && !awaitingColumnMapping && (
          <div style={{ marginBottom: 12 }}>
            <Button
              type="link"
              onClick={() => {
                setDraftRows([]);
                setPreviewReady(false);
                setSourceBusinessId(undefined);
                setAwaitingColumnMapping(true);
              }}
            >
              Modifier la correspondance des colonnes
            </Button>
          </div>
        )}

        {draftRows.length > 0 && !awaitingColumnMapping && !previewReady && (
          <Card type="inner" title="Étape 2 — Boutique de référence (optionnel)" style={{ marginBottom: 16 }}>
            {businessesListPending || (businessesListFetching && businessesList.length === 0) ? (
              <>
                <Paragraph type="secondary" style={{ marginBottom: 12 }}>
                  Chargement de la liste des boutiques…
                </Paragraph>
                <Spin />
              </>
            ) : (
              <>
                <Paragraph type="secondary" style={{ marginBottom: 12 }}>
                  Sélectionnez la boutique dont les produits serviront de <strong>référence</strong> : à l’aperçu,
                  la colonne « Produit référence » proposera les articles au <strong>nom similaire</strong> pour
                  compléter prix, marque et description des produits à créer sur <Text strong>{businessName}</Text>.
                  Laissez vide pour n’utiliser que le fichier.
                </Paragraph>

                {!hasReferenceBusiness && (
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="Aucune autre boutique disponible"
                    description="La liste ne contient aucune autre boutique active (ou aucune n’est accessible depuis ce compte). Vous pouvez continuer : l’import utilisera uniquement le fichier."
                  />
                )}

                <Select
                  allowClear
                  showSearch
                  disabled={!hasReferenceBusiness}
                  optionFilterProp="label"
                  placeholder="Aucune — uniquement les données du fichier"
                  value={sourceBusinessId}
                  onChange={(v) => setSourceBusinessId(v)}
                  options={sourceBusinessOptions}
                  style={{ width: '100%', maxWidth: 560, marginBottom: 16 }}
                />

                <Button type="primary" onClick={() => setPreviewReady(true)}>
                  Afficher l’aperçu des lignes
                </Button>
              </>
            )}
          </Card>
        )}

        {isSuperAdmin && (
          <>
            <Divider orientation="left">Super Admin</Divider>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="Seuil d’alerte stock sur tout le catalogue"
              description={
                <>
                  Applique le même <strong>minStockAlert</strong> à <strong>tous les produits</strong> de ce business
                  (y compris inactifs). Si le multi-sites inventaire est activé et qu’il existe des lieux actifs, le
                  seuil est aussi écrit pour chaque lieu dans <strong>minStockAlertBySite</strong>.
                </>
              }
            />
            <Space wrap style={{ marginBottom: 16 }}>
              <Button
                icon={<ThunderboltOutlined />}
                onClick={() => {
                  setBulkMinStockValue(defaultMinStockAlert);
                  setBulkMinStockModalOpen(true);
                }}
              >
                Appliquer un seuil à tous les produits…
              </Button>
            </Space>
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="Suppression totale des produits"
              description={
                <>
                  Supprime définitivement <strong>tous les produits</strong> de ce business. Les{' '}
                  <strong>ventes produits</strong> et <strong>mouvements de stock</strong> liés à chaque produit
                  sont supprimés en même temps. Ensuite vous pourrez désactiver les catégories vides depuis
                  l’onglet Catégories (Excel).
                </>
              }
            />
            <Button
              danger
              icon={<DeleteOutlined />}
              loading={purgeProductsMutation.isPending}
              onClick={() => {
                setPurgeConfirmName('');
                setPurgeModalOpen(true);
              }}
            >
              Supprimer tous les produits…
            </Button>
            <Modal
              title="Confirmer la suppression des produits"
              open={purgeModalOpen}
              onCancel={() => {
                setPurgeModalOpen(false);
                setPurgeConfirmName('');
              }}
              okText="Supprimer"
              okButtonProps={{ danger: true, loading: purgeProductsMutation.isPending }}
              onOk={runPurgeProducts}
              destroyOnClose
            >
              <Paragraph>
                Pour confirmer, saisissez exactement le nom du business : <Text strong>{businessName}</Text>
              </Paragraph>
              <Input
                value={purgeConfirmName}
                onChange={(e) => setPurgeConfirmName(e.target.value)}
                placeholder="Nom du business"
                autoComplete="off"
              />
            </Modal>

            <Modal
              title={`Seuil d’alerte stock — ${businessName}`}
              open={bulkMinStockModalOpen}
              onCancel={() => {
                if (!applyMinStockAllMutation.isPending) {
                  setBulkMinStockModalOpen(false);
                }
              }}
              okText="Appliquer à tous les produits"
              cancelText="Annuler"
              okButtonProps={{ loading: applyMinStockAllMutation.isPending }}
              cancelButtonProps={{ disabled: applyMinStockAllMutation.isPending }}
              onOk={handleConfirmBulkMinStock}
              destroyOnClose
            >
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                  Tous les documents produits avec <Text code>businessId</Text> = ce business seront mis à jour
                  (actifs et inactifs).
                  {multiSitesEnabled && activeSiteIds.length > 0 ? (
                    <>
                      {' '}
                      Lieux actifs concernés pour <Text code>minStockAlertBySite</Text> : {activeSiteIds.length}.
                    </>
                  ) : null}
                </Paragraph>
                <Space wrap align="center">
                  <Text>Seuil (unités)</Text>
                  <InputNumber
                    min={0}
                    precision={0}
                    value={bulkMinStockValue ?? undefined}
                    onChange={(v) => setBulkMinStockValue(typeof v === 'number' ? v : null)}
                    style={{ width: 160 }}
                  />
                </Space>
              </Space>
            </Modal>
          </>
        )}

        {previewReady && !awaitingColumnMapping && validationIssues.length > 0 && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 12 }}
            message={`${validationIssues.length} ligne(s) à corriger avant import.`}
          />
        )}

        {previewReady && draftRows.length > 0 && hasReferenceBusiness && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={
              sourceBusinessId
                ? `Rapprochement depuis : ${
                    sourceBusinessOptions.find((o) => o.value === sourceBusinessId)?.label ?? sourceBusinessId
                  }`
                : 'Aucune boutique de référence — création uniquement à partir du fichier.'
            }
            action={
              <Button size="small" type="link" onClick={() => setPreviewReady(false)}>
                Modifier l’étape boutique
              </Button>
            }
          />
        )}

        <Spin spinning={loading}>
          <Table<ProductImportDraftRow>
            size="small"
            rowKey="rowIndex"
            scroll={{ x: hasReferenceBusiness && sourceBusinessId ? 1620 : 1300 }}
            pagination={previewReady && draftRows.length > 15 ? { pageSize: 15 } : false}
            dataSource={previewReady ? draftRows : []}
            columns={columns}
            components={{
              body: {
                cell: (props: React.TdHTMLAttributes<HTMLTableCellElement>) => (
                  <td {...props} style={{ ...props.style, verticalAlign: 'top' }} />
                ),
              },
            }}
            locale={{
              emptyText: awaitingColumnMapping
                ? 'Validez la correspondance des colonnes pour continuer.'
                : !previewReady && draftRows.length > 0
                  ? 'Terminez l’étape « Boutique de référence » (ou cliquez sur « Afficher l’aperçu des lignes ») pour voir les lignes ici.'
                  : 'Aucune donnée importée. Chargez un fichier Excel.',
            }}
          />
        </Spin>

        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button
            onClick={() => {
              setDraftRows([]);
              setPreviewReady(false);
              setSourceBusinessId(undefined);
              if (importMatrix) setAwaitingColumnMapping(true);
              else {
                setColumnMappingForm({});
              }
            }}
            disabled={(!draftRows.length && !importMatrix) || importMutation.isPending}
          >
            Vider l’aperçu / reprendre la correspondance
          </Button>
          <Button
            type="primary"
            loading={importMutation.isPending}
            disabled={
              blocked ||
              !defaultPos?.id ||
              categories.length === 0 ||
              importMutation.isPending ||
              awaitingColumnMapping
            }
            onClick={submitImport}
          >
            Créer les produits
          </Button>
        </div>
      </Card>

      <Modal
        title="Création des produits"
        open={importModalOpen}
        closable={importProgress?.phase === 'done' || !!importProgress?.fatalMessage}
        maskClosable={importProgress?.phase === 'done' || !!importProgress?.fatalMessage}
        onCancel={importProgress?.phase === 'done' || importProgress?.fatalMessage ? closeImportModal : undefined}
        footer={
          importProgress?.phase === 'done' || importProgress?.fatalMessage ? (
            <Button type="primary" onClick={closeImportModal}>
              Fermer
            </Button>
          ) : null
        }
        destroyOnClose
      >
        {importProgress?.fatalMessage ? (
          <Alert type="error" showIcon message={importProgress.fatalMessage} />
        ) : importProgress ? (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Progress
              percent={
                importProgress.total > 0
                  ? Math.min(100, Math.round((importProgress.processed / importProgress.total) * 100))
                  : 0
              }
              status={
                importProgress.phase === 'done'
                  ? importProgress.errors.length > 0
                    ? 'exception'
                    : 'success'
                  : 'active'
              }
            />
            <Text type="secondary">
              {importProgress.phase === 'running'
                ? `Traitement : ${importProgress.label || '…'} — ${importProgress.processed} / ${importProgress.total} ligne(s) traitée(s)`
                : `${importProgress.created} produit(s) créé(s) sur ${importProgress.total} ligne(s).`}
            </Text>
            {importProgress.errors.length > 0 && (
              <>
                <Alert
                  type="warning"
                  showIcon
                  message="Lignes en erreur"
                  description="Chaque entrée indique le nom du produit (ou la ligne), le numéro de ligne dans l’aperçu et le message renvoyé par Firestore ou la validation."
                />
                <List
                  size="small"
                  bordered
                  dataSource={importProgress.errors}
                  style={{ maxHeight: 280, overflow: 'auto' }}
                  renderItem={(err) => (
                    <List.Item>
                      <div>
                        <Text strong>{err.name}</Text>
                        <Text type="secondary"> — ligne {err.rowIndex + 1}</Text>
                        <div style={{ marginTop: 4, fontSize: 13 }}>{err.message}</div>
                      </div>
                    </List.Item>
                  )}
                />
              </>
            )}
          </Space>
        ) : null}
      </Modal>
    </div>
  );
};

export default ProductsImportExportTabContent;
